import * as Yup from "yup";
import { CURRENCY } from "../../../../components/Constants";
import { ContainerSize, SelfDocuments } from "../../../../components/Enums";

// types
export interface SalesInvoiceProducts {
  product_id: string;
  price: number;
  quantity: number;
  quantity_unit_id: string;
  total_amount: string;
  packing_type_id: string;
  packing_unit_id: string;
  no_of_bags: number;
  packing_net_weight: number;
  packing_gross_weight: number;
}

export const containerInitialValues = {
  product_id: "",
  container_no: "",
  sales_contract_id: "",
  lot_no: "",
  lot_label_seal: "",
  line_seal_no: "",
  self_seal_id: "",
  sticker_no: "",
  vehicle_no: "",
  size: "",
  quantity: "",
  no_of_bags: "",
  packing_type_id: "",
  total_net_weight: "",
  net_weight_unit_id: "",
  total_gross_weight: "",
  gross_weight_unit_id: "",
  remark: "",
};

export const otherDocumentInitialValues = {
  document_id: "",
  files: [],
  remove_files: [],
};

export const myDocumentsInputInitialValues = {
  [SelfDocuments.PHYTOSANITARY_CERTIFICATE]: {
    psc_no: "",
    registration_no: "",
    date_of_treatment: "",
    treatment: "",
    chemical: "",
    duration: "",
    temperature: "",
    concentration: "",
    additional_information: "",
    date_of_issue: "",
    place_of_issue: "",
    code_no: "",
  },
  [SelfDocuments.HEALTH_AND_QUALITY_CERTIFICATE]: {
    lab_name: "",
    certificate_no: "",
    certificate_date: "",
  },
  [SelfDocuments.PRIOR_NOTICE]: {
    main_reference_no: "",
    certificate_reference_no: "",
    certificate_issue_date: "",
    nfsca_body: "",
    reference_no: "",
    place_of_issue: "",
    date_of_issue: "",
    place_of_applicant: "",
    date_of_applicant: "",
  },
  [SelfDocuments.FORM_AI]: {
    place_of_issue: "",
    reference_no: "",
    port_of_discharge: "",
  },
  [SelfDocuments.FUMIGATION_CERTIFICATE]: {
    date: "",
    registration_no: "",
    treatment_certificate_no: "",
    place_of_fumigation: "",
  },
  [SelfDocuments.ANNEXURE_C]: {
    factory_address: "",
    date_of_examination: "",
    date_of_sealing: "",
  },
  [SelfDocuments.BILL_OF_LADING]: {
    booking_no: "",
    line: "",
    sb_no: "",
    sb_date: "",
  },
  [SelfDocuments.COMMERCIAL_INVOICE]: {
    show_total_fields: false,
    freight: "",
    insurance: "",
    discount: "",
  },
};
export type MyDocumentsInputsType = typeof myDocumentsInputInitialValues;

export const salesInvoiceProductValues = {
  product_id: "",
  sales_contract_id: "",
  price: "",
  quantity: "",
  // quantity_unit_id: "",
  total_amount: "",
  no_of_containers: "",
  container_load: "",
  packing_type_id: "",
  // packing_unit_id: "",
  no_of_bags: "",
  packing_net_weight: "",
  packing_gross_weight: "",
  total_net_weight: "",
  total_gross_weight: "",
};

export const salesInvoiceInitialValues = {
  sales_contract_ids: [],
  buyer_id: "",
  invoice_no: "",
  invoice_date: null,
  logistic_id: "",
  cha_id: "",
  is_notify_party: false,
  notify_party_id: "",
  status: "",
  sales_invoice_products: [salesInvoiceProductValues],

  currency: CURRENCY.USD.NAME,
  total_quantity: 0,
  total_amount: 0,
  exchange_rate: "",
  total_taxable_amount_inr: "",
  igst_percentage: "",
  total_igst_amount_inr: "",
  commission_rate: "",
  commission_amount: "",
  total_no_of_bags: "",
  total_net_weight: "",
  total_gross_weight: "",
  sales_invoice_supplier_products: [],
  bl_no: "",
  vessel_no: "",
  container_size: "",
  pre_carriage_by: "",
  origin_port_id: "",
  port_of_loading_code: "",
  etd: null,
  destination_port: "",
  eta: null,
  delivery_term: "",
  payment_term: "",
  container: [],
  my_documents: [],
  my_documents_inputs: myDocumentsInputInitialValues,
  other_documents: [],
  bills: [],
};
export type SalesInvoiceInitialValues = typeof salesInvoiceInitialValues;

export const salesInvoiceSupplierProductValidationSchema = Yup.object().shape({
  product_id: Yup.string().required("product is required"),
  purchase_id: Yup.string().required("purchase order is required"),
  quantity: Yup.number()
    .required("quantity is required")
    .moreThan(0, "Quantity must be greater than 0"),
  // unit_id: Yup.string().nullable(),
});

export const containerValidationSchema = Yup.object().shape({
  product_id: Yup.string().required("Product is required"),
  container_no: Yup.string().required("Container no is required"),
  lot_no: Yup.string().nullable(),
  lot_label_seal: Yup.string().nullable(),
  line_seal_no: Yup.string().nullable(),
  self_seal_id: Yup.string().nullable(),
  sticker_no: Yup.string().nullable(),
  vehicle_no: Yup.string().nullable(),
  size: Yup.string().nullable(),
  no_of_bags: Yup.string()
    .nullable()
    .test(
      "is-valid-decimal",
      "No. of bags must be a positive value",
      function (value: any) {
        return !(value < 0);
      },
    ),
  total_net_weight: Yup.string()
    .nullable()
    .test(
      "is-valid-decimal",
      "Total net weight must be a positive value",
      function (value: any) {
        return !(value < 0);
      },
    ),
  total_gross_weight: Yup.string()
    .nullable()
    .test(
      "is-valid-decimal",
      "Total gross weight must be a positive value",
      function (value: any) {
        return !(value < 0);
      },
    ),
  remark: Yup.string().nullable(),
});

export const otherDocumentValidationSchema = Yup.object().shape({
  document_id: Yup.string().required("Document is required"),
  files: Yup.array().optional(),
  remove_files: Yup.array().optional(),
});

export const billValidationSchema = Yup.object().shape({
  bill_id: Yup.string().required("Bill is required"),
  files: Yup.array().optional(),
  remove_files: Yup.array().optional(),
});

export const productValidationSchema = Yup.object().shape({
  product_id: Yup.string().required("Product is required"),
  price: Yup.number()
    .required("Price is required")
    .moreThan(0, "price must be greater than 0"),
  quantity: Yup.number()
    .required("Quantity is required")
    .moreThan(0, "quantity must be greater than 0"),
  // quantity_unit_id: Yup.string().notRequired(),
  total_amount: Yup.number()
    .required("total_amount is required")
    .moreThan(0, "total amount must be greater than 0"),
  no_of_containers: Yup.number()
    .moreThan(0, "no_of_containers must be greater than 0")
    .nullable(),
  container_load: Yup.string().nullable(),
  packing_type_id: Yup.string().notRequired(),
  // packing_unit_id: Yup.string().notRequired(),
  no_of_bags: Yup.number()
    .moreThan(0, "no_of_bags must be greater than 0")
    .nullable(),
  packing_net_weight: Yup.number()
    .moreThan(0, "packing_net_weight must be greater than 0")
    .nullable(),
  total_net_weight: Yup.number()
    .moreThan(0, "total_net_weight must be greater than 0")
    .nullable(),
  packing_gross_weight: Yup.number()
    .moreThan(0, "packing_gross_weight must be greater than 0")
    .nullable(),
  total_gross_weight: Yup.number()
    .moreThan(0, "total_gross_weight must be greater than 0")
    .nullable(),
});

export const salesInvoiceValidationSchema = Yup.object().shape({
  sales_contract_ids: Yup.array()
    .of(Yup.string())
    .min(1, "At least one sales contract is required"),
  buyer_id: Yup.string().required("Buyer is required"),
  invoice_no: Yup.string().required("Invoice no is required"),
  invoice_date: Yup.date().required("Invoice date is required"),
  status: Yup.string().required("Status is required"),
  logistic_id: Yup.string(),
  cha_id: Yup.string(),
  is_notify_party: Yup.boolean(),
  notify_party_id: Yup.string().when("is_notify_party", {
    is: (value: boolean) => value,
    then: (schema: any) => schema.required("Notify party is required"),
  }),

  sales_invoice_products: Yup.array()
    .of(productValidationSchema)
    .min(1, "At least one product is required"),

  currency: Yup.string().required("Currency is required"),
  total_quantity: Yup.number()
    .moreThan(0, "total_quantity must be greater than 0")
    .required("Quantity is required"),
  total_amount: Yup.number().required("Total amount is required"),
  exchange_rate: Yup.number()
    .moreThan(0, "exchange_rate must be greater than 0")
    .nullable(),
  total_taxable_amount_inr: Yup.number()
    .moreThan(0, "total_taxable_amount_inr must be greater than 0")
    .nullable(),
  igst_percentage: Yup.number()
    .moreThan(0, "igst_percentage must be greater than 0")
    .nullable(),
  total_igst_amount_inr: Yup.number()
    .moreThan(0, "total_igst_amount_inr must be greater than 0")
    .nullable(),
  commission_rate: Yup.number()
    .moreThan(0, "commission_rate must be greater than 0")
    .nullable(),
  commission_amount: Yup.number()
    .moreThan(0, "commission_amount must be greater than 0")
    .nullable(),
  total_no_of_bags: Yup.number()
    .moreThan(0, "total_no_of_bags must be greater than 0")
    .nullable(),
  total_net_weight: Yup.number()
    .moreThan(0, "total_net_weight must be greater than 0")
    .nullable(),
  total_gross_weight: Yup.number()
    .moreThan(0, "total_gross_weight must be greater than 0")
    .nullable(),
  bl_no: Yup.string(),
  vessel_no: Yup.string(),
  pre_carriage_by: Yup.string(),
  origin_port_id: Yup.string().optional().nullable(),
  port_of_loading_code: Yup.string().optional().nullable(),
  etd: Yup.date().optional().nullable(),
  destination_port: Yup.string().optional().nullable(),
  eta: Yup.date().optional().nullable(),
  delivery_term: Yup.string(),
  payment_term: Yup.string(),

  container: Yup.array().of(containerValidationSchema),
  my_documents: Yup.array()
    .of(Yup.string().required("Invalid document selection!"))
    .nullable()
    .optional(),
  other_documents: Yup.array().of(otherDocumentValidationSchema),
  bills: Yup.array().of(billValidationSchema),
  sales_invoice_supplier_products: Yup.array().of(
    salesInvoiceSupplierProductValidationSchema,
  ),
});
